import { useRef } from 'react';
import { ABOUT } from '@/data/about';
import { PARTNERS, type PartnerLogo } from '@/data/partners';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import { Img } from '@/components/Img';

const GLOBE = '/landing/partners/globe-new.webp';
const BG_GRID = '/landing/partners/bg-grid.webp';

/** The band's paragraph is written on the About page in the CMS. */
const paragraph = (): string => ABOUT.partners.intro;

const HEADING_GRADIENT = 'linear-gradient(92.84deg, #1b1b1b 9.48%, #a6a6a6 57.18%, #b7b7b7 97.32%)';

/** A partner's mark, linked to their site when the CMS has one. */
function Logo({ logo, className }: { logo: PartnerLogo; className: string }) {
    const img = <Img src={logo.logo} alt={logo.name} className={className} />;
    return logo.href ? (
        <a href={logo.href} target="_blank" rel="noreferrer" aria-label={logo.name}>
            {img}
        </a>
    ) : (
        img
    );
}

function PartnersDesktop({ logos }: { logos: PartnerLogo[] }) {
    return (
        <FigmaFrame designWidth={1920} designHeight={815} className="w-full">
            <div className="relative h-[815px] w-[1920px] overflow-hidden bg-[#f5f5f5]">
                {/* Background artwork (faint grid) */}
                <Img src={BG_GRID} alt="" className="pointer-events-none absolute inset-0 size-full object-cover" />

                {/* Globe graphic */}
                <Reveal
                    as="div"
                    once
                    variant="fade"
                    duration={1300}
                    className="absolute left-[1043px] top-[108px] h-[320px] w-[555px] overflow-hidden"
                >
                    <Img
                        src={GLOBE}
                        alt=""
                        className="pointer-events-none absolute inset-0 size-full object-contain object-center"
                    />
                </Reveal>

                {/* Heading + paragraph block */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[118px] top-[98px] h-[411px] w-[854px]"
                >
                    <p className="absolute left-[358px] top-[115px] w-[496px] text-[16px] font-normal leading-[24px] text-[#151515]">
                        {paragraph()}
                    </p>
                    <div className="absolute left-0 top-[205.5px] flex w-[854px] -translate-y-1/2 flex-col">
                        <span
                            className="bg-clip-text text-[150px] font-light leading-[167px] text-transparent"
                            style={{ backgroundImage: HEADING_GRADIENT }}
                        >
                            Our{' '}
                        </span>
                        <span className="text-[150px] leading-[167px]">
                            <span className="font-bold text-[#1b1b1b]">Partners</span>
                            <span className="font-medium text-[#f60]">.</span>
                        </span>
                    </div>
                </Reveal>

                {/* Partner logos, in the CMS's wall order */}
                <div className="absolute left-[137px] top-[607px] flex w-[1646px] flex-wrap items-center gap-x-[64px] gap-y-[40px]">
                    {logos.map((logo, i) => (
                        <Reveal key={logo.name} once variant="fade-up" delay={i * 90}>
                            <Logo logo={logo} className="h-[62px] w-auto max-w-[260px] shrink-0 object-contain" />
                        </Reveal>
                    ))}
                </div>
            </div>
        </FigmaFrame>
    );
}

/** Chevron glyph for the mobile logo carousel. */
const Chevron = ({ direction }: { direction: 'left' | 'right' }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

function PartnersMobile({ logos }: { logos: PartnerLogo[] }) {
    const logosRef = useRef<HTMLDivElement>(null);
    const scrollLogos = (dir: number) => {
        const el = logosRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
    };

    return (
        <section className="relative overflow-hidden bg-[#f5f5f5] pt-[31px]">
            {/* Background artwork (faint grid) */}
            <Img src={BG_GRID} alt="" className="pointer-events-none absolute inset-0 size-full object-cover" />

            <div className="relative pb-[31px]">
                {/* Heading */}
                <h2 className="px-5 flex flex-col text-[40px] leading-[50px]">
                    <span className="font-normal text-[#6c6c6c]">Our</span>
                    <span>
                        <span className="font-extrabold text-[#1b1b1b]">Partners</span>
                        <span className="font-extrabold text-[#f60]">.</span>
                    </span>
                </h2>

                {/* Paragraph */}
                <p className="mt-[10px] px-5 max-w-[336px] text-[14px] font-normal leading-[24px] text-[#151515]">
                    {paragraph()}
                </p>

                {/* Globe graphic */}
                <div className="relative mx-auto mt-[31px] h-[118px] w-[254px] overflow-hidden">
                    <Img
                        src={GLOBE}
                        alt=""
                        className="pointer-events-none absolute inset-0 size-full object-contain object-center"
                    />
                </div>

                {/* Company logos — swipeable strip with chevron controls */}
                <div
                    ref={logosRef}
                    className="scrollbar-hide mt-[42px] flex items-center gap-[28px] overflow-x-auto scroll-smooth px-5"
                >
                    {logos.map((logo) => (
                        <Logo
                            key={logo.name}
                            logo={logo}
                            className="h-[26px] w-auto max-w-none shrink-0 object-contain"
                        />
                    ))}
                </div>

                <div className="mt-[22px] flex items-center justify-center gap-[26px]">
                    <button
                        type="button"
                        aria-label="Previous partners"
                        onClick={() => scrollLogos(-1)}
                        className="flex size-[34px] items-center justify-center rounded-full bg-[#e6e6e6] text-[#1b1b1b]"
                    >
                        <Chevron direction="left" />
                    </button>
                    <button
                        type="button"
                        aria-label="Next partners"
                        onClick={() => scrollLogos(1)}
                        className="flex size-[34px] items-center justify-center rounded-full bg-[#e6e6e6] text-[#1b1b1b]"
                    >
                        <Chevron direction="right" />
                    </button>
                </div>
            </div>
        </section>
    );
}

/** Read at render so the hydrated wall is what shows; nothing to show, nothing rendered. */
export default function Partners() {
    const logos = PARTNERS;
    if (!logos.length) return null;
    return (
        <>
            <div className="hidden lg:block">
                <PartnersDesktop logos={logos} />
            </div>
            <div className="lg:hidden">
                <PartnersMobile logos={logos} />
            </div>
        </>
    );
}
